/**
 * ARCTIC ARMOR™ B2C Landing Page JavaScript
 * Handles smooth scrolling, add to cart, and analytics
 */

class ArcticArmorB2C {
  constructor() {
    this.init();
  }

  init() {
    this.setupSmoothScroll();
    this.setupAddToCart();
    this.setupAnalytics();
    this.setupIntersectionObserver();
  }

  /**
   * Smooth scroll for anchor links
   */
  setupSmoothScroll() {
    const smoothScrollLinks = document.querySelectorAll('.smooth-scroll');
    
    smoothScrollLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href');
        const targetElement = document.querySelector(targetId);
        
        if (targetElement) {
          const headerOffset = 80;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Track smooth scroll event
          this.trackEvent('Navigation', 'Smooth Scroll', targetId);
        }
      });
    });
  }

  /**
   * Add to cart functionality
   */
  setupAddToCart() {
    const addToCartButtons = document.querySelectorAll('.aa-product-card__cta');
    
    if (addToCartButtons.length === 0) {
      console.log('No add-to-cart buttons found');
      return;
    }

    console.log(`Found ${addToCartButtons.length} add-to-cart buttons`);
    
    addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        
        const variantId = button.dataset.variantId;
        const productId = button.dataset.productId;
        
        console.log('Add to cart clicked:', { variantId, productId });
        
        if (!variantId) {
          console.error('No variant ID found on button');
          alert('Product variant not found. Please refresh the page and try again.');
          return;
        }

        // Update button state
        const originalText = button.textContent;
        button.textContent = 'Adding...';
        button.disabled = true;

        try {
          // Add to cart via Shopify Cart API
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: parseInt(variantId),
                quantity: 1
              }]
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Failed to add to cart');
          }

          const data = await response.json();
          console.log('Added to cart successfully:', data);

          // Success state
          button.textContent = '✓ Added!';
          button.style.background = '#28a745';
          
          // Track add to cart event
          this.trackEvent('E-commerce', 'Add to Cart', productId);
          
          // Update cart count
          this.updateCartCount();

          // Trigger Shopify theme cart refresh events
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));

          // Trigger cart drawer if it exists (Dawn theme)
          if (typeof window.theme !== 'undefined' && window.theme.cart) {
            window.theme.cart.refresh();
          }

          // Alternative cart drawer triggers
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          // Reset button after 2 seconds
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
            button.style.background = '';
          }, 2000);

        } catch (error) {
          console.error('Add to cart error:', error);
          
          // Error state
          button.textContent = 'Error - Try Again';
          button.style.background = '#dc3545';
          
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
            button.style.background = '';
          }, 2000);
        }
      });
    });
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update all cart count elements
      const cartCountElements = document.querySelectorAll(
        '.cart-count, [data-cart-count], .cart-count-bubble, #cart-icon-bubble'
      );
      
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        if (cart.item_count > 0) {
          element.classList.remove('hidden');
        }
      });

      console.log('Cart count updated:', cart.item_count);
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Setup analytics tracking
   */
  setupAnalytics() {
    // Track page view
    this.trackPageView();

    // Track CTA clicks
    const ctaButtons = document.querySelectorAll('.aa-btn');
    ctaButtons.forEach(button => {
      button.addEventListener('click', () => {
        const buttonText = button.textContent.trim();
        this.trackEvent('CTA', 'Click', buttonText);
      });
    });

    // Track testimonial views
    this.trackTestimonialViews();

    // Track scroll depth
    this.trackScrollDepth();
  }

  /**
   * Track page view
   */
  trackPageView() {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_view', {
        page_title: 'ARCTIC ARMOR™ B2C Landing Page',
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }

    // Shopify Analytics
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.lib.track('Viewed Landing Page', {
        page: 'ARCTIC ARMOR™ B2C'
      });
    }
  }

  /**
   * Track events
   */
  trackEvent(category, action, label = '', value = null) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      const eventParams = {
        event_category: category,
        event_label: label
      };
      
      if (value !== null) {
        eventParams.value = value;
      }

      gtag('event', action, eventParams);
    }

    // Shopify Analytics
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.lib.track(action, {
        category: category,
        label: label,
        value: value
      });
    }

    // Console log for debugging
    console.log('Analytics Event:', { category, action, label, value });
  }

  /**
   * Track testimonial views using Intersection Observer
   */
  trackTestimonialViews() {
    const testimonials = document.querySelectorAll('.aa-testimonial-card');
    const trackedTestimonials = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !trackedTestimonials.has(entry.target)) {
          const authorName = entry.target.querySelector('.aa-testimonial__author strong')?.textContent || 'Unknown';
          this.trackEvent('Engagement', 'Testimonial View', authorName);
          trackedTestimonials.add(entry.target);
        }
      });
    }, {
      threshold: 0.5
    });

    testimonials.forEach(testimonial => observer.observe(testimonial));
  }

  /**
   * Track scroll depth
   */
  trackScrollDepth() {
    const milestones = [25, 50, 75, 100];
    const tracked = new Set();

    const calculateScrollPercentage = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      return Math.round(scrollPercentage);
    };

    const handleScroll = () => {
      const scrollPercentage = calculateScrollPercentage();

      milestones.forEach(milestone => {
        if (scrollPercentage >= milestone && !tracked.has(milestone)) {
          this.trackEvent('Engagement', 'Scroll Depth', `${milestone}%`, milestone);
          tracked.add(milestone);
        }
      });
    };

    // Throttle scroll events
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /**
   * Setup Intersection Observer for animations
   */
  setupIntersectionObserver() {
    const animatedElements = document.querySelectorAll('.aa-problem__card, .aa-step, .aa-product-card, .aa-testimonial-card');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    animatedElements.forEach(element => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(30px)';
      element.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      observer.observe(element);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArcticArmorB2C();
  });
} else {
  new ArcticArmorB2C();
}

// Handle Shopify section reloads in theme editor
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ArcticArmorB2C();
  });
}
